import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { AppUserModalStyles } from 'components/pages/app/users/modals/styles'
import { useAlert } from 'hooks/useAlert'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { AppUserServiceUpdateRequest, AppUserType } from 'pbts/c1/api/app/v1/app_user'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import React, { ChangeEvent, FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SetAppUserAccountTypeApi } from 'redux_toolkit/app-users'

export type SetAccountTypeFormProps = {
    appUser: AppUserValue
} & ClosableProps

export const SetAccountTypeModal: FC<SetAccountTypeFormProps> = ({ close, appUser }) => {
    const toast = useAlert()
    const intl = useIntl()
    const { mutate: updateAppUser } = SetAppUserAccountTypeApi()
    const [appUserType, setAppUserType] = useState<AppUserType>(appUser.appUserType)

    const handleSave = useCallback((): void => {
        updateAppUser(
            AppUserServiceUpdateRequest.create({
                appUser: { ...appUser, appUserType },
                updateMask: FieldMask.create({
                    paths: ['app_user_type'],
                }),
                expandMask: { paths: ['*'] },
            }),
        )
            .then(() => {
                toast(
                    intl.formatMessage({
                        id: 'app_user_type_updated',
                        defaultMessage: 'Account type updated',
                    }),
                    'success',
                )
                close()
            })
            .catch((err) =>
                toast(
                    intl.formatMessage(
                        {
                            id: 'app_user_type_update_error',
                            defaultMessage: 'Error setting account type. {code}: {msg}',
                        },
                        {
                            code: (err as RpcError).code,
                            msg: (err as RpcError).message,
                        },
                    ),
                    'error',
                ))

        close()
    }, [appUser, appUserType, close, intl, toast, updateAppUser])

    return (
        <C1Dialog open onClose={close}>
            <C1DialogTitle>
                <FormattedMessage id="component_set_account_type_form_title" defaultMessage="Set account type" />
            </C1DialogTitle>
            <C1DialogContent>
                <Typography>
                    <FormattedMessage
                        id="component_set_account_type_form_description"
                        defaultMessage="Update the account type to either service account or user. You can only select one option at a time."
                    />
                </Typography>
                <Box sx={AppUserModalStyles.content}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">
                            <FormattedMessage id="account_type_radio" defaultMessage="Account type" />
                        </FormLabel>
                        <RadioGroup
                            aria-label="application"
                            name="application"
                            onChange={(event: ChangeEvent<{ value: unknown }>): void => {
                                switch (event.target.value as string) {
                                    case 'user_account_selected':
                                        setAppUserType(AppUserType.USER)
                                        break
                                    case 'service_account_selected':
                                        setAppUserType(AppUserType.SERVICE_ACCOUNT)
                                        break
                                    case 'system_account_selected':
                                        setAppUserType(AppUserType.SYSTEM_ACCOUNT)
                                    // no default
                                }
                            }}
                        >
                            <FormControlLabel
                                checked={appUserType === AppUserType.USER}
                                value="user_account_selected"
                                control={<Radio />}
                                label={intl.formatMessage({
                                    id: 'app_user_type_user',
                                    defaultMessage: 'User',
                                })}
                            />
                            <FormControlLabel
                                checked={appUserType === AppUserType.SERVICE_ACCOUNT}
                                value="service_account_selected"
                                control={<Radio />}
                                label={intl.formatMessage({
                                    id: 'app_user_type_service_account',
                                    defaultMessage: 'Service account',
                                })}
                            />
                            <FormControlLabel
                                checked={appUserType === AppUserType.SYSTEM_ACCOUNT}
                                value="system_account_selected"
                                control={<Radio />}
                                label={intl.formatMessage({
                                    id: 'app_user_type_system_account',
                                    defaultMessage: 'System account',
                                })}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            </C1DialogContent>
            <C1DialogActions>
                <C1Button onClick={close} variant="outlined">
                    <FormattedMessage id="component_mappings_cancel" defaultMessage="Cancel" />
                </C1Button>
                <C1Button onClick={handleSave} color="primary" variant="contained">
                    <FormattedMessage id="component_mappings_set_mappings" defaultMessage="Update account type" />
                </C1Button>
            </C1DialogActions>
        </C1Dialog>
    )
}
